import { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { LoadingOverlay, Modal } from "@/components";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { CustomColumnTypes } from "@/enum/list.enum";
import { InputField } from "@/components/InputField/InputField";

import textIcon from "@/assets/icons/textIcon.svg?url";
import numbersIcon from "@/assets/icons/numbersIcon.svg?url";
import linkIcon from "@/assets/icons/linkIcon.svg?url";
import aiIcon from "@/assets/icons/aiIcon.svg?url";
import checkboxIcon from "@/assets/icons/checkboxIcon.svg?url";
import diceIcon from "@/assets/icons/diceIcon.svg?url";
import googleNewsIcon from "@/assets/icons/googleNewsIcon.svg?url";
import departmentIcon from "@/assets/icons/sidebar/site-map.svg?url";
import toolIcon from "@/assets/icons/sidebar/screw-driver-wrench.svg?url";
import linkedinIcon from "@/assets/icons/linkedin.svg?url";
import jobIcon from "@/assets/icons/job.svg?url";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { ButtonColor } from "@/components/Button/types";

import { useAddColumnToTrackerMutation } from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { ColumnConfig } from "../../../../List/schema/columnConfig";
import { CustomColumn, CustomColumnSchema } from "../../../../List/schema/list";
import { AiModal } from "./AIModal";
import { DepartmentModal } from "./DepartmentModal";
import { GoogleNewsModal } from "./GoogleNewsModal";
import { JobOfferModal } from "./JobOfferModal";
import { LinkedinPostModal } from "./LinkedinPostModal";
import { RandomValueModal } from "./RandomValueModal";
import { TechnologyModal } from "./TechnologyModal";
import { Tracker } from "../../../schema/profileTracker";

export interface EditCustomColumnModalProps {
	setOpen: (value: boolean) => void;
	open: boolean;
	trackerId: number;
	columnId: number;
	column: ColumnConfig;
	selectedTrackerOrganizationIds?: number[];
	tracker?: Tracker;
}

export const EditCustomColumnModal: FunctionComponent<EditCustomColumnModalProps> = ({
	open,
	setOpen,
	trackerId,
	columnId,
	column,
	selectedTrackerOrganizationIds,
	tracker,
}) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);
	const [columnType, setColumnType] = useState<CustomColumnTypes>();

	const [showConfirm, setShowConfirm] = useState<boolean>(false);

	const options = useMemo(
		() => [
			{
				title: ts(CustomColumnTypes.TEXT),
				id: CustomColumnTypes.TEXT,
				icon: <img alt="textIcon" className="mr-1 w-[20px] h-[20px]" src={textIcon} />,
			},
			{
				title: ts(CustomColumnTypes.NUMBER),
				id: CustomColumnTypes.NUMBER,
				icon: <img alt="numbersIcon" className="mr-1" src={numbersIcon} />,
			},
			{
				title: ts(CustomColumnTypes.LINK),
				id: CustomColumnTypes.LINK,
				icon: <img alt="linkIcon" className="mr-1 w-[20px] h-[20px]" src={linkIcon} />,
			},
			{
				title: ts(CustomColumnTypes.BOOLEAN),
				id: CustomColumnTypes.BOOLEAN,
				icon: <img alt="linkIcon" className="mr-1 w-[20px] h-[20px]" src={checkboxIcon} />,
			},
			{
				title: ts(CustomColumnTypes.AI_PROMPT),
				id: CustomColumnTypes.AI_PROMPT,
				icon: <img alt="linkIcon" className="mr-1 w-[20px] h-[20px]" src={aiIcon} />,
			},
			{
				title: ts(CustomColumnTypes.RANDOM_VALUE),
				id: CustomColumnTypes.RANDOM_VALUE,
				icon: <img alt="linkIcon" className="mr-1 w-[20px] h-[20px]" src={diceIcon} />,
			},
			{
				title: ts(CustomColumnTypes.GOOGLE_NEWS),
				id: CustomColumnTypes.GOOGLE_NEWS,
				icon: <img alt="linkIcon" className="mr-1 w-[20px] h-[20px]" src={googleNewsIcon} />,
			},
			{
				title: ts(CustomColumnTypes.DEPARTMENT_SIZE),
				id: CustomColumnTypes.DEPARTMENT_SIZE,
				icon: <img alt="linkIcon" className="mr-1 w-[20px] h-[20px]" src={departmentIcon} />,
			},
			{
				title: ts(CustomColumnTypes.LINKEDIN_POST),
				id: CustomColumnTypes.LINKEDIN_POST,
				icon: <img alt="linkIcon" className="mr-1 w-[20px] h-[20px]" src={linkedinIcon} />,
			},
			{
				title: ts(CustomColumnTypes.JOB_OFFER),
				id: CustomColumnTypes.JOB_OFFER,
				icon: <img alt="linkIcon" className="mr-1 w-[20px] h-[20px]" src={jobIcon} />,
			},
			{
				title: ts(CustomColumnTypes.TECHNOLOGY),
				id: CustomColumnTypes.TECHNOLOGY,
				icon: <img alt="linkIcon" className="mr-1 w-[20px] h-[20px]" src={toolIcon} />,
			},
		],
		[ts]
	);

	const [update, { isLoading, error }] = useAddColumnToTrackerMutation();

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<CustomColumn>({
		defaultValues: {
			customColumnName: column?.name,
			customColumnType: column?.type,
			value: column?.config,
		},
		resolver: zodResolver(CustomColumnSchema),
	});

	const onSubmit = async (values: CustomColumn) => {
		try {
			await update({
				id: trackerId,
				...values,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	return !column ? (
		<LoadingOverlay />
	) : column ? (
		<Modal
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={open}
			overflow={true}
			size="xs"
			submitButtonText={t("basics.confirm")}
			subtitle="Change the name and Config of your column"
			title="Edit Column"
		>
			<div className="flex flex-col space-y-4">
				<InputField
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("customColumnName", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Column Name"
					name={"name"}
					placeholder="Enter column name"
					value={getValues("customColumnName") || column?.name || ""}
				/>

				<Dropdown
					data={options}
					disabled={true}
					floating={true}
					handleSelect={function (value?: AutoCompleteItem) {
						if (value?.id) {
							setValue("customColumnType", value.id as CustomColumnTypes);
							setColumnType(value.id as CustomColumnTypes);
						}
					}}
					label="Custom Column Type"
					value={options.find((option: AutoCompleteItem) => option.id === column?.type)}
				/>

				{columnType === CustomColumnTypes.RANDOM_VALUE && (
					<RandomValueModal
						column={column}
						columnId={columnId}
						open={true}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
						trackerId={trackerId}
					/>
				)}

				{columnType === CustomColumnTypes.TECHNOLOGY && (
					<TechnologyModal
						column={column}
						columnId={columnId}
						open={true}
						selectedTrackerOrganizationIds={selectedTrackerOrganizationIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
						tracker={tracker}
						trackerId={trackerId}
					/>
				)}

				{columnType === CustomColumnTypes.DEPARTMENT_SIZE && (
					<DepartmentModal
						column={column}
						columnId={columnId}
						open={true}
						selectedTrackerOrganizationIds={selectedTrackerOrganizationIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
						tracker={tracker}
						trackerId={trackerId}
					/>
				)}

				{columnType === CustomColumnTypes.GOOGLE_NEWS && (
					<GoogleNewsModal
						column={column}
						columnId={columnId}
						open={true}
						selectedTrackerOrganizationIds={selectedTrackerOrganizationIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
						tracker={tracker}
						trackerId={trackerId}
					/>
				)}

				{columnType === CustomColumnTypes.AI_PROMPT && (
					<AiModal
						column={column}
						columnId={columnId}
						open={true}
						selectedTrackerOrganizationIds={selectedTrackerOrganizationIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
						trackerId={trackerId}
					/>
				)}

				{columnType === CustomColumnTypes.LINKEDIN_POST && (
					<LinkedinPostModal
						column={column}
						columnId={columnId}
						open={true}
						selectedTrackerOrganizationIds={selectedTrackerOrganizationIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
						tracker={tracker}
						trackerId={trackerId}
					/>
				)}

				{columnType === CustomColumnTypes.JOB_OFFER && (
					<JobOfferModal
						column={column}
						columnId={columnId}
						open={true}
						selectedTrackerOrganizationIds={selectedTrackerOrganizationIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
						tracker={tracker}
						trackerId={trackerId}
					/>
				)}

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</div>
			{showConfirm && (
				<Modal
					handleClose={() => setShowConfirm(false)}
					handleSave={async () => {
						setShowConfirm(false);
						setOpen(false);
					}}
					isOpened={showConfirm}
					size="xs"
					submitButtonColor={ButtonColor.ACTION}
					submitButtonText={t("basics.confirm")}
					title={t("basics.confirm")}
				>
					<>{t("basics.confirmationClose")} </>
				</Modal>
			)}
		</Modal>
	) : (
		<></>
	);
};
